/* eslint-disable react-hooks/exhaustive-deps */
import './antd-table.less';

import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
  Text,
} from '@tonic-one/react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useDashboardServicesStore } from '../../../../models/useDashboardServicesModel';
import { getServiceNamesByValues } from '../../../../utils/service';
import { generateRedirectUrlToLog } from '../../../Log/shared';
import DashboardCard from '../../Layout/DashboardCard';
import { useGlobalSettingStore } from '../../../../models/useGlobalSettingModel';
import RedirectLink from '@Components/RedirectLink';
import { LogTypeEnum, SecurityFilterName } from '@Types/log';
interface DataType {
  level: string;
  key: number;
  filePercent: string;
  urlPercent: string;
  file: number;
  url: number;
}

function VirtualAnalyzerRiskSummary(props: { data: any; day: string }) {
  const { t } = useTranslation();
  const servicesModel = useDashboardServicesStore();
  const { selectedOrgGlobalSetting } = useGlobalSettingStore();

  const data: DataType[] = useMemo(() => {
    let data = formatTableData(
      props.data?.virtual_analyzerSummary?.[props.day]?.FILE || [],
      props.data?.virtual_analyzerSummary?.[props.day]?.URL || []
    );
    if (data && data.length > 0) {
      data.forEach((item: any) => {
        item.file =
          item.file === 0 ? (
            0
          ) : (
            <RedirectLink
              href={`logs?${generateRedirectUrlToLog(
                [
                  {
                    field: 'securityFilterName',
                    values: [SecurityFilterName.VirtualAnalyzer],
                    type: 'terms',
                  },
                  {
                    field: 'ddaaasRiskLevel',
                    values:
                      item.level !== 'UNKNOWN'
                        ? [item.level]
                        : [
                            'Unrated',
                            'DDAaaS Other error',
                            'Scan canceled',
                            'Network Timeout',
                            'Network Circuit Break',
                            'RMS Decrypt Failed',
                            'UNKNOWN',
                            'unsupported file type in ROZ',
                          ],
                    type: 'terms',
                  },
                  {
                    field: 'ddaaasScanType',
                    values: ['FILE'],
                    type: 'terms',
                  },
                  {
                    field: 'action',
                    values: ['NoAction', 'PassByWhiteList'],
                    type: 'mustNot',
                    subType: 'terms',
                  },
                  {
                    field: 'scanSource',
                    values: getServiceNamesByValues(
                      servicesModel.enabledServices
                    ),
                    type: 'terms',
                  },
                ],
                props.day,
                LogTypeEnum.DETECTION
              )}`}
            >
              <Text>{item.file}</Text>
            </RedirectLink>
          );
        item.url =
          item.url === 0 ? (
            0
          ) : (
            <RedirectLink
              href={`logs?${generateRedirectUrlToLog(
                [
                  {
                    field: 'securityFilterName',
                    values: [SecurityFilterName.VirtualAnalyzer],
                    type: 'terms',
                  },
                  {
                    field: 'ddaaasRiskLevel',
                    values:
                      item.level !== 'UNKNOWN'
                        ? [item.level]
                        : [
                            'Unrated',
                            'DDAaaS Other error',
                            'Scan canceled',
                            'Network Timeout',
                            'Network Circuit Break',
                            'RMS Decrypt Failed',
                            'UNKNOWN',
                            'unsupported file type in ROZ',
                          ],
                    type: 'terms',
                  },
                  {
                    field: 'ddaaasScanType',
                    values: ['URL'],
                    type: 'terms',
                  },
                  {
                    field: 'action',
                    values: ['NoAction', 'PassByWhiteList'],
                    type: 'mustNot',
                    subType: 'terms',
                  },
                  {
                    field: 'scanSource',
                    values: getServiceNamesByValues(
                      servicesModel.enabledServices
                    ),
                    type: 'terms',
                  },
                ],
                props.day,
                LogTypeEnum.DETECTION
              )}`}
            >
              <Text>{item.url}</Text>
            </RedirectLink>
          );
      });
    }
    data = data.map((item) => {
      switch (item.level) {
        case 'High Risk':
          return { ...item, level: t('dashboard.risk.highRisk'), key: 0 };
        case 'Medium Risk':
          return { ...item, level: t('dashboard.risk.mediumRisk'), key: 1 };
        case 'Low Risk':
          return { ...item, level: t('dashboard.risk.lowRisk'), key: 2 };
        case 'No Risk':
          return { ...item, level: t('dashboard.risk.noRisk'), key: 3 };
        case 'UNKNOWN':
          return { ...item, level: t('dashboard.risk.unknown'), key: 4 };
        default:
          throw new Error('Unexpected risk level');
      }
    });
    return data as DataType[];
  }, [props.data, props.day]);

  const columns: ColumnDef<DataType>[] = useMemo(() => {
    const _columns: ColumnDef<DataType>[] = [
      {
        id: 'level',
        accessorKey: 'level',
        header: '',
        size: 100,
      },
      {
        id: 'filePercent',
        accessorKey: 'filePercent',
        header: t('dashboard.chart.column.percentage'),
        size: 100,
        meta: {
          customColumnStyle: {
            textAlign: 'right',
          },
        },
      },
      {
        id: 'file',
        accessorKey: 'file',
        header: t('dashboard.chart.column.detections'),
        cell: ({ row }) => {
          return row.original.file;
        },
        size: 100,
        meta: {
          customColumnStyle: {
            textAlign: 'right',
          },
        },
      },
    ];
    if (selectedOrgGlobalSetting.urlSandboxFeatureShow) {
      return _columns.concat([
        {
          id: 'urlPercent',
          accessorKey: 'urlPercent',
          header: t('dashboard.chart.column.percentage'),
          size: 100,
          meta: {
            customColumnStyle: {
              textAlign: 'right',
            },
          },
        },
        {
          id: 'url',
          accessorKey: 'url',
          header: t('dashboard.chart.column.detections'),
          cell: ({ row }) => {
            return row.original.url;
          },
          size: 100,
          meta: {
            customColumnStyle: {
              textAlign: 'right',
            },
          },
        },
      ]);
    }
    return _columns;
  }, [selectedOrgGlobalSetting]);

  const table = useReactTable({
    columns,
    data,
    defaultColumn: {
      minSize: 40,
    },
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <DashboardCard
      height="324px"
      title={t('dashboard.chart.title.VirtualAnalyzerRiskSummary')}
    >
      <Box height="100%">
        <Table layout="flexbox" width="100%">
          {selectedOrgGlobalSetting.urlSandboxFeatureShow && (
            <TableHeader>
              <TableHeaderRow width="100%">
                <TableHeaderCell key="riskLevel">
                  {t('dashboard.chart.column.riskLevel')}
                </TableHeaderCell>
                <TableHeaderCell key="file" textAlign="center" width="40%">
                  {t('dashboard.chart.column.file')}
                </TableHeaderCell>

                <TableHeaderCell key="url" textAlign="center" width="40%">
                  {t('dashboard.chart.column.url')}
                </TableHeaderCell>
              </TableHeaderRow>
            </TableHeader>
          )}
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableHeaderRow key={headerGroup.id} width="100%">
                {headerGroup.headers.map((header) => {
                  const styleProps = {
                    minWidth: header.column.columnDef.minSize,
                    width: selectedOrgGlobalSetting.urlSandboxFeatureShow
                      ? '20%'
                      : '40%',
                    ...header.column.columnDef.meta?.customColumnStyle,
                  };
                  if (
                    header.id === 'level' &&
                    !selectedOrgGlobalSetting.urlSandboxFeatureShow
                  ) {
                    return (
                      <TableHeaderCell key="riskLevel" width="40%">
                        {t('dashboard.chart.column.riskLevel')}
                      </TableHeaderCell>
                    );
                  } else {
                    return (
                      <TableHeaderCell key={header.id} {...styleProps}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </TableHeaderCell>
                    );
                  }
                })}
              </TableHeaderRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                width="100%"
                // _hover={{
                //   backgroundColor: hoverBackgroundColor,
                // }}
              >
                {row.getVisibleCells().map((cell) => {
                  const styleProps = {
                    minWidth: cell.column.columnDef.minSize,
                    // width: cell.column.getSize(),
                    width: selectedOrgGlobalSetting.urlSandboxFeatureShow
                      ? '20%'
                      : '40%',
                    ...cell.column.columnDef.meta?.customColumnStyle,
                  };
                  return (
                    <TableCell key={cell.id} {...styleProps}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </DashboardCard>
  );
}

function formatTableData(
  files: [{ key: string; doc_count: number }],
  urls: [{ key: string; doc_count: number }]
) {
  const levels: { [key: string]: { file: number; url: number } } = {
    'High Risk': { file: 0, url: 0 },
    'Medium Risk': { file: 0, url: 0 },
    'Low Risk': { file: 0, url: 0 },
    'No Risk': { file: 0, url: 0 },
    UNKNOWN: { file: 0, url: 0 },
  };

  let fileTotal = 0;
  for (const item of files) {
    const level = levels[item.key];
    if (level) {
      level.file = item.doc_count || 0;
      fileTotal += level.file;
    }
  }

  let urlTotal = 0;
  for (const item of urls) {
    const level = levels[item.key];
    if (level) {
      level.url = item.doc_count || 0;
      urlTotal += level.url;
    }
  }

  return Object.entries(levels).map(([key, level]) => ({
    level: key,
    ...level,
    filePercent: formatPercent(level.file, fileTotal),
    urlPercent: formatPercent(level.url, urlTotal),
  }));
}

function formatPercent(num: number, total: number) {
  if (total === 0) {
    return '-';
  }
  if (num / total < 0.01) {
    return '< 1%';
  }
  return ((num / total) * 100).toFixed(2) + '%';
}

export default VirtualAnalyzerRiskSummary;
